import { app, authentication } from '@microsoft/teams-js'

export type HostKind = 'web' | 'teams'
export type TeamsThemeName = 'default' | 'dark' | 'contrast' | 'glass'

/**
 * Detects whether the app is running inside a Microsoft Teams tab (iframe
 * with a Teams parent) versus a normal browser tab. `app.initialize()` only
 * resolves if a Teams host responds to its handshake; loaded standalone it
 * never resolves at all, so a plain `await` would hang forever — hence the
 * fast pre-check (not in any iframe ⇒ definitely not Teams, skip waiting)
 * and the timeout race as a fallback for iframes that aren't Teams.
 */
export async function detectHost(timeoutMs = 800): Promise<HostKind> {
  const inIframe = typeof window !== 'undefined' && window.self !== window.top
  if (!inIframe) return 'web'

  try {
    await Promise.race([
      app.initialize(),
      new Promise((_resolve, reject) => setTimeout(() => reject(new Error('teams_init_timeout')), timeoutMs)),
    ])
    return 'teams'
  } catch {
    return 'web'
  }
}

/** Reads the current Teams context; only meaningful after detectHost() returned 'teams'. */
export async function getTeamsContext(): Promise<app.Context> {
  return app.getContext()
}

export function normalizeTeamsTheme(raw: string | undefined): TeamsThemeName {
  if (raw === 'dark' || raw === 'contrast' || raw === 'glass') return raw
  return 'default'
}

/** Subscribes to live Teams theme changes (light/dark/high-contrast toggled from Teams settings). */
export function onTeamsThemeChange(handler: (theme: TeamsThemeName) => void): void {
  app.registerOnThemeChangeHandler((theme) => handler(normalizeTeamsTheme(theme)))
}

/** Tells Teams the tab finished loading, so it stops showing its own loading spinner. */
export function notifyTeamsAppLoaded(): void {
  app.notifySuccess()
}

/**
 * Applies the route requested by Teams when a suspended personal tab resumes.
 * The origin check prevents a host message from navigating Sales elsewhere.
 */
export function applyTeamsContentUrl(contentUrl: URL | string): boolean {
  const target = new URL(contentUrl.toString(), window.location.origin)
  if (target.origin !== window.location.origin) return false

  const nextPath = `${target.pathname}${target.search}${target.hash}`
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextPath !== currentPath) {
    window.history.replaceState(null, '', nextPath)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }
  return true
}

/** Keeps the installed Teams tab aligned with its configured /hoy entry. */
export function prepareTeamsEntry(teamsContext: app.Context): void {
  const hasDeepLinkState = Boolean(teamsContext.page.subPageId)
  if (!hasDeepLinkState) applyTeamsContentUrl('/hoy')

  try {
    app.lifecycle.registerBeforeSuspendOrTerminateHandler(async () => {})
    app.lifecycle.registerOnResumeHandler((context) => {
      applyTeamsContentUrl(context.contentUrl)
      app.notifySuccess()
    })
  } catch (error) {
    console.warn('[SCC][Teams] lifecycle handlers unavailable:', error)
  }
}

/**
 * Teams classic SSO: asks the Teams host itself for an access token issued
 * for this app's API (the `webApplicationInfo.resource` in the Teams
 * manifest — api://sales.wacner.com/<api-client-id>). The token carries
 * aud=<api-client-id> and scp=access_as_user, exactly what the backend
 * validates, so it goes straight into the X-Wacner-Access-Token header
 * with no MSAL involved. This is the pre-NAA, officially supported SSO
 * path for Teams tabs; it requires the API app registration to
 * pre-authorize the Microsoft Teams/Office client apps on that scope.
 */
export async function getTeamsAuthToken(): Promise<string> {
  return authentication.getAuthToken()
}
